import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useReservoirClient, } from "@reservoir0x/reservoir-kit-ui";
import { setParams } from "@reservoir0x/reservoir-sdk";
import dayjs from "dayjs";
import { parseUnits } from "viem";
import { useConfig, useWalletClient } from "wagmi";
import { getAccount, switchChain } from "wagmi/actions";
import { useListingCartContext } from "./ListingCartProvider";
import getLocalMarketplaceData from "./getLocalMarketplaceData";
import useChainCurrency from "./useChainCurrency";
export var ListStep;
(function (ListStep) {
    ListStep[ListStep["Unavailable"] = 0] = "Unavailable";
    ListStep[ListStep["SetPrice"] = 1] = "SetPrice";
    ListStep[ListStep["Listing"] = 2] = "Listing";
    ListStep[ListStep["Complete"] = 3] = "Complete";
})(ListStep || (ListStep = {}));
export const defaultExpirationOptions = [
    {
        text: "1 Hour",
        value: "hour",
        relativeTime: 1,
        relativeTimeUnit: "h",
    },
    {
        text: "12 Hours",
        value: "12 hours",
        relativeTime: 12,
        relativeTimeUnit: "h",
    },
    {
        text: "1 Day",
        value: "1 day",
        relativeTime: 1,
        relativeTimeUnit: "d",
    },
    {
        text: "3 Day",
        value: "3 days",
        relativeTime: 3,
        relativeTimeUnit: "d",
    },
    { text: "1 Week", value: "week", relativeTime: 1, relativeTimeUnit: "w" },
    { text: "1 Month", value: "month", relativeTime: 1, relativeTimeUnit: "M" },
    {
        text: "3 Months",
        value: "3 months",
        relativeTime: 3,
        relativeTimeUnit: "M",
    },
    {
        text: "6 Months",
        value: "6 months",
        relativeTime: 6,
        relativeTimeUnit: "M",
    },
];
export const expirationOptions = [
    ...defaultExpirationOptions,
    {
        text: "Custom",
        value: "custom",
        relativeTime: null,
        relativeTimeUnit: null,
    },
];
const useListingCart = (tokensToList = [], targetChainId) => {
    const [listStep, setListStep] = useState(ListStep.SetPrice);
    const [globalError, setGlobalError] = useState(null);
    const [stepData, setStepData] = useState(null);
    const [marketplacesCache, setMarketplacesCache] = useState({});
    const { dispatch } = useListingCartContext();
    const chainCurrency = useChainCurrency(targetChainId);
    const pendingRequests = useRef(new Set());
    const marketplacesCacheRef = useRef(marketplacesCache);
    // Keep ref updated with latest cache state
    useEffect(() => {
        marketplacesCacheRef.current = marketplacesCache;
    }, [marketplacesCache]);
    const tokenKeys = useMemo(() => tokensToList.map((t) => `${targetChainId}:${t.address}:${t.tokenId}`), [tokensToList, targetChainId]);
    const client = useReservoirClient();
    const { data: wallet } = useWalletClient();
    const config = useConfig();
    useEffect(() => {
        if (!client || !tokensToList.length)
            return;
        tokensToList.forEach((token) => {
            const cacheKey = `${token.address}:${token.tokenId}`;
            // Skip if already cached or request pending
            if (marketplacesCacheRef.current[cacheKey] !== undefined ||
                pendingRequests.current.has(cacheKey)) {
                return;
            }
            pendingRequests.current.add(cacheKey);
            const fetchData = async () => {
                try {
                    const marketplaces = await fetchMarketplace(client, token.address, token.tokenId, targetChainId);
                    const filteredMarketplaces = marketplaces.filter((m) => m.orderbook !== "x2y2" &&
                        Object.values((m === null || m === void 0 ? void 0 : m.exchanges) || {}).some((e) => e.enabled));
                    setMarketplacesCache((prev) => ({
                        ...prev,
                        [cacheKey]: filteredMarketplaces,
                    }));
                }
                catch (error) {
                    setMarketplacesCache((prev) => ({
                        ...prev,
                        [cacheKey]: [],
                    }));
                }
                finally {
                    pendingRequests.current.delete(cacheKey);
                }
            };
            fetchData();
        });
    }, [client, tokensToList, targetChainId]);
    const listTokens = useCallback(async () => {
        try {
            setGlobalError(null);
            setListStep(ListStep.Listing);
            if (!wallet || !client) {
                throw new Error("Wallet or client not connected");
            }
            if (!tokensToList.every((token) => token.price && token.expirationOption && token.quantity)) {
                throw new Error("Price, expiration on quantity is not set for all tokens");
            }
            const currentChain = getAccount(config).chain;
            if ((currentChain === null || currentChain === void 0 ? void 0 : currentChain.id) !== targetChainId) {
                await switchChain(config, { chainId: targetChainId });
            }
            const listings = tokensToList.map((token) => {
                var _a;
                const cacheKey = `${token.address}:${token.tokenId}`;
                const marketplaces = marketplacesCache[cacheKey];
                const marketplace = marketplaces[0];
                const exchanges = Object.values((marketplace === null || marketplace === void 0 ? void 0 : marketplace.exchanges) || {});
                const exchange = exchanges.find((exchange) => (exchange === null || exchange === void 0 ? void 0 : exchange.orderKind) === "seaport-v1.6" && (exchange === null || exchange === void 0 ? void 0 : exchange.enabled)) || exchanges.find((exchange) => exchange === null || exchange === void 0 ? void 0 : exchange.enabled);
                if (!marketplace || !(exchange === null || exchange === void 0 ? void 0 : exchange.enabled)) {
                    throw new Error(`Marketplace not found for token ${cacheKey}`);
                }
                if (!token.price || !token.quantity) {
                    throw new Error(`Listings settings not found for token ${cacheKey}`);
                }
                if (!chainCurrency) {
                    throw new Error(`Chain currency could not be fetched`);
                }
                const options = {
                    [`${exchange.orderKind}`]: {
                        useOffChainCancellation: true,
                    },
                };
                return {
                    token: `${token.address}:${token.tokenId}`,
                    weiPrice: (parseUnits(token.price, chainCurrency.decimals || 18) *
                        BigInt(token.quantity)).toString(),
                    options,
                    orderKind: exchange.orderKind,
                    orderbook: marketplace.orderbook,
                    expirationTime: ((_a = token.expirationOption) === null || _a === void 0 ? void 0 : _a.relativeTime)
                        ? dayjs()
                            .add(token.expirationOption.relativeTime, token.expirationOption
                            .relativeTimeUnit)
                            .unix()
                            .toString()
                        : undefined,
                    currency: chainCurrency.address,
                    quantity: token.quantity,
                    marketplaceFees: token.marketplaceFee,
                    royaltyBps: token.royaltyBps,
                };
            });
            await client.actions.listToken({
                chainId: targetChainId,
                listings,
                wallet,
                onProgress: (steps) => {
                    const executableSteps = steps.filter((step) => { var _a; return (_a = step.items) === null || _a === void 0 ? void 0 : _a.length; });
                    const currentStep = executableSteps[executableSteps.length - 1];
                    setStepData({
                        totalSteps: executableSteps.length,
                        stepProgress: executableSteps.length,
                        currentStep,
                    });
                },
            });
            setListStep(ListStep.Complete);
        }
        catch (error) {
            dispatch({
                type: "SET_ERROR",
                payload: { chainId: targetChainId, error: error },
            });
            setGlobalError(error);
            setListStep(ListStep.SetPrice);
        }
    }, [
        tokensToList,
        tokenKeys,
        client,
        wallet,
        config,
        targetChainId,
        chainCurrency,
    ]);
    const loading = useMemo(() => tokensToList.some((token) => {
        const cacheKey = `${token.address}:${token.tokenId}`;
        return (!marketplacesCache[cacheKey] && pendingRequests.current.has(cacheKey));
    }), [tokensToList, marketplacesCache]);
    return {
        loading,
        listStep,
        transactionError: globalError,
        stepData,
        listTokens,
    };
};
export default useListingCart;
async function fetchMarketplace(client, collectionId, tokenId, chainId) {
    const chain = (client === null || client === void 0 ? void 0 : client.chains.find((c) => c.id === chainId)) || (client === null || client === void 0 ? void 0 : client.currentChain());
    if (!chain || !collectionId)
        return [];
    const url = new URL(`${chain.baseApiUrl}/collections/${collectionId}/marketplace-configurations/v2`);
    const query = {};
    if (tokenId) {
        query.tokenId = tokenId;
        setParams(url, query);
    }
    const response = await fetch(url.href, {
        headers: {
            "x-api-key": (client === null || client === void 0 ? void 0 : client.apiKey) || "",
            "Content-Type": "application/json",
        },
    });
    if (!response.ok)
        return [];
    const data = await response.json();
    const marketplaces = data.marketplaces;
    const localData = getLocalMarketplaceData();
    return marketplaces.map((marketplace) => {
        var _a;
        if (marketplace.orderbook === "reservoir") {
            return {
                ...marketplace,
                name: localData.title,
                domain: client === null || client === void 0 ? void 0 : client.source,
                imageUrl: (_a = localData.icon) !== null && _a !== void 0 ? _a : undefined,
            };
        }
        return marketplace;
    });
}
